namespace distill.dashboards {
    import BASE_PROPERTIES = appn.designerv2.BASE_PROPERTIES;
    import PROPERTY_TYPE = appn.designerv2.PROPERTY_TYPE;

    bindPieGeometryConstraints();

    const asDash = (c: IComponent) => c as IDashboardComponent;
    const COLOR_PROPERTIES: { [key: string]: IPropertyDefinition } = {
        colorPalette: {
            name: T('Color Palette'),
            type: 'colorPalette',
            tooltip: T('Choose a color scheme for the chart series.'),
            defaultValue: 'Vibrant',
            sector: 'layout',
            path: 'styling.colorPalette' },
        bgColor: {
            name: T('Background Color'),
            type: 'colorSelector',
            tooltip: T('Fill color for the entire widget container.'),
            defaultValue: 'transparent',
            sector: 'layout'
        },
        headerBgColor: {
            name: T('Header Background'),
            type: 'colorSelector',
            tooltip: T('Background color specifically for the chart title bar area.'),
            defaultValue: 'transparent',
            sector: 'layout',
            path: 'styling.headerBgColor'
        },
        outlineColor: {
            name: T('Outline Color'),
            type: 'colorSelector',
            tooltip: T('Color of the borders separating chart segments (e.g. pie slices).'),
            defaultValue: '#ffffff',
            sector: 'layout',
            path: 'styling.outlineColor'
        },
    };
    const GLOBAL_FILTER_BINDINGS_PROPERTY: { [key: string]: IPropertyDefinition } = {
        globalFilterBindings: {
            name: T('Global Filter Bindings'),
            type: 'globalFilterBindings',
            tooltip: T("Maps each filter to a column of this widget's datasets. Every filter must reach this widget either through a binding or through a filter criterion in its Filters or Summary Filters. A filter criterion overrides the binding."),
            defaultValue: [],
            sector: 'data',
            path: 'dataQuery0.globalFilterBindings'
        }
    };
    const LEGEND_PROPERTIES: { [key: string]: IPropertyDefinition } = {
        legendGroup: {
            name: T('Legend'),
            type: 'group',
            sector: 'layout',
            defaultValue: null,
            tooltip: ''
        },
        showLegend: {
            name: T('Show Legend'),
            type: 'bool',
            tooltip: T('Toggle visibility of the series key.'),
            defaultValue: true,
            sector: 'layout',
            path: 'layout.showLegend'
        },
        legendStyle: {
            name: T('Legend Position'),
            type: 'legendStyle',
            tooltip: T('Adjust where the legend appears relative to the chart.'),
            defaultValue: { orient: 'horizontal', verticalPosition: 'bottom', horizontalPosition: 'center' },
            sector: 'layout',
            path: 'layout.legendStyle',
            visibleWhen: (c) => asDash(c).layout?.showLegend !== false
        },
        ...createFormattingProperties({
            groupPrefix: 'legend',
            displayPrefix: T('Legend'),
            pathRoot: 'styling.legendValueFormatting',
            visibleWhen: (c) => asDash(c).layout?.showLegend !== false,
            defaultFormat: 'Category',
            defaultStyle: { color: '#666666', size: 'Medium', decor: 'Normal' },
            allowedFormats: ['Category', 'Value', 'Category: Value']
        })
    };
    const DATA_LABEL_PROPERTIES: { [key: string]: IPropertyDefinition } = {
        labelsGroup: {
            name: T('Data Labels'),
            type: 'group',
            sector: 'layout',
            defaultValue: null,
            tooltip: '' },
        showDataLabels: {
            name: T('Show Data Labels'),
            type: 'bool',
            tooltip: T('Overlay values directly on chart elements.'),
            defaultValue: false,
            sector: 'layout',
            path: 'layout.showDataLabels'
        },
        ...createFormattingProperties({
            groupPrefix: 'dataLabel',
            displayPrefix: T('Data Label'),
            pathRoot: 'styling.dataLabelValueFormatting',
            visibleWhen: (c) => asDash(c).layout?.showDataLabels === true,
            defaultStyle: { color: '#333333', size: 'Medium', decor: 'Normal' }
        })
    };
    const TOOLTIP_STYLE_PROPERTIES: { [key: string]: IPropertyDefinition } = {
        tooltipBgColor: {
            name: T('Tooltip Background Color'),
            type: 'colorSelector',
            tooltip: T('Background color of the hover info box.'),
            defaultValue: '#323232',
            sector: 'layout',
            path: 'styling.tooltipBgColor',
            visibleWhen: (c) => asDash(c).styling?.tooltipTrigger !== 'None'
        },
        tooltipBgOpacity: {
            name: T('Tooltip Background Opacity (%)'),
            type: 'numeric',
            tooltip: T('Transparency of the hover info box (0 = invisible, 100 = solid).'),
            defaultValue: 95,
            sector: 'layout',
            path: 'styling.tooltipBgOpacity',
            config: { appearance: 'slider', min: 0, max: 100 },
            visibleWhen: (c) => asDash(c).styling?.tooltipTrigger !== 'None'
        },
        ...createFormattingProperties({
            groupPrefix: 'tooltip',
            displayPrefix: T('Tooltip'),
            pathRoot: 'styling.tooltipValueFormatting',
            visibleWhen: (c) => asDash(c).styling?.tooltipTrigger !== 'None',
            defaultFormat: 'Category: Value',
            defaultStyle: { color: '#ffffff', size: 'Small', decor: 'Normal' }
        })
    };
    const CARTESIAN_GEOMETRY_PROPERTIES: { [key: string]: IPropertyDefinition } = {
        enableCustomGeometry: {
            name: T('Customize Geometry'),
            type: 'bool',
            tooltip: T('Manually adjust the size and position of the chart.'),
            defaultValue: false,
            sector: 'layout',
            path: 'layout.enableCustomGeometry'
        },
        chartMarginTop: {
            name: T('Top (%)'),
            type: 'numeric',
            sector: 'layout',
            defaultValue: 15,
            config: { appearance: 'slider', min: 0, max: 50 },
            tooltip: T('Adjust top whitespace to prevent label clipping.'),
            path: 'layout.chartMarginTop',
            visibleWhen: (c) => asDash(c).layout?.enableCustomGeometry === true
        },
        chartMarginBottom: {
            name: T('Bottom (%)'),
            type: 'numeric',
            sector: 'layout',
            defaultValue: 10,
            config: { appearance: 'slider', min: 0, max: 50 },
            tooltip: T('Adjust bottom whitespace to accommodate axis labels.'),
            path: 'layout.chartMarginBottom',
            visibleWhen: (c) => asDash(c).layout?.enableCustomGeometry === true
        },
        chartMarginLeft: {
            name: T('Left (%)'),
            type: 'numeric',
            sector: 'layout',
            defaultValue: 10,
            config: { appearance: 'slider', min: 0, max: 50 },
            tooltip: T('Adjust left whitespace for Y-axis labels.'),
            path: 'layout.chartMarginLeft',
            visibleWhen: (c) => asDash(c).layout?.enableCustomGeometry === true
        },
        chartMarginRight: {
            name: T('Right (%)'),
            type: 'numeric',
            sector: 'layout',
            defaultValue: 10,
            config: { appearance: 'slider', min: 0, max: 50 },
            tooltip: T('Adjust right whitespace.'),
            path: 'layout.chartMarginRight',
            visibleWhen: (c) => asDash(c).layout?.enableCustomGeometry === true
        },
    };

    componentManager.registerSectors([{
        id: 'basic',
        label: T('Basic'),
        icon: 'fa-list-ul'
    }, {
        id: 'data',
        label: T('Data'),
        icon: 'fa-dice-d6'
    }, {
        id: 'filters',
        label: T('Global Filters'),
        icon: 'fa-filter'
    }, {
        id: 'layout',
        label: T('Layout & Styling'),
        icon: 'fa-paint-brush'
    }, {
        id: 'advanced',
        label: T('Advanced'),
        icon: 'fa-graduation-cap'
    }]);

    componentManager.registerDefinitions({
        'titleBar': {
            title: T('Title Bar'), description: '', icon: '', category: 'Widgets',
            properties: {
                title: {
                    name: T('Dashboard Title'),
                    type: 'uiText',
                    tooltip: T('The main heading displayed at the top of the dashboard.'),
                    defaultValue: { eng: 'Dashboard' },
                    sector: 'basic'
                },
                hideTitle: {
                    name: T('Hide Title'),
                    type: 'bool',
                    tooltip: T('Completely hide the title bar area.'),
                    defaultValue: false,
                    sector: 'basic'
                },
                refreshInterval: {
                    name: T('Refresh Rate'),
                    type: 'enum',
                    tooltip: T('How often the dashboard automatically reloads data.'),
                    defaultValue: '5m',
                    options: ['30s', '1m', '5m', '15m', '1h'],
                    sector: 'basic'
                },
                globalFilters: {
                    name: '',
                    type: 'globalFiltersEditor',
                    // No tooltip popover — the hint is rendered inline by the editor body.
                    tooltip: '',
                    defaultValue: null,
                    sector: 'filters'
                },

                // Typography mirrors the heading component so the title can be styled the same way.
                typographyGroup: {
                    name: T('Typography'),
                    type: 'group',
                    sector: 'layout',
                    defaultValue: null,
                    tooltip: ''
                },
                headingAlign: {
                    name: T('Alignment'),
                    type: 'enum',
                    defaultValue: 'Center',
                    options: ['Left', 'Center', 'Right'],
                    sector: 'layout',
                    tooltip: T('Horizontal alignment of the title.'),
                    path: 'layout.headingAlign'
                },
                headingSize: {
                    name: T('Font Size'),
                    type: 'numeric',
                    defaultValue: 24,
                    sector: 'layout',
                    config: { min: 8, max: 120 },
                    tooltip: T('Size of the title text.'),
                    path: 'styling.headingSize'
                },
                headingColor: {
                    name: T('Text Color'),
                    type: 'colorSelector',
                    defaultValue: '#2c3e50',
                    sector: 'layout',
                    tooltip: T('Color of the title text.'),
                    path: 'styling.headingColor'
                },
                isBold: {
                    name: T('Bold'),
                    type: 'bool',
                    defaultValue: true,
                    sector: 'layout',
                    tooltip: T('Apply bold weight.'),
                    path: 'styling.isBold'
                },
                isItalic: {
                    name: T('Italic'),
                    type: 'bool',
                    defaultValue: false,
                    sector: 'layout',
                    tooltip: T('Apply italic style.'),
                    path: 'styling.isItalic'
                },

                bgColor: {
                    name: T('Background Color'),
                    type: 'colorSelector',
                    tooltip: T('Fill color for the title bar.'),
                    defaultValue: 'transparent',
                    sector: 'layout'
                },
            },
            render: titleBarFn
        },
        'group': {
            title: T('Group'), description: '', icon: '', category: 'Widgets',
            properties: {}
        },
        'pieChart': {
            title: T('Pie Chart'), description: T('Display data as a pie chart.'), icon: 'fa-chart-pie', category: 'Charts',
            properties: {
                ...BASE_PROPERTIES,

                // --- Data ---
                datasetSelector: {
                    name: T('Datasets'),
                    type: 'datasetSelector',
                    tooltip: T('Select the datasets(s) to use for this component.'),
                    sector: 'data',
                    defaultValue: null,
                    path: 'dataQuery0'
                },
                ...GLOBAL_FILTER_BINDINGS_PROPERTY,
                filtersEditor: {
                    name: T('Filters'),
                    type: 'filtersEditor',
                    tooltip: T(`Restrict data based on specific criteria (e.g. Status = 'Active').`),
                    sector: 'data',
                    defaultValue: null,
                    path: 'dataQuery0.filter'
                },
                groupBy: {
                    name: T('Group By'),
                    type: 'columnSelector',
                    tooltip: T(`Categorize the data slices (e.g. by 'Status' or 'Department').`),
                    sector: 'data',
                    defaultValue: null,
                    path: 'dataQuery0.aggregation.groupBy'
                },
                metrics: {
                    name: T('Metric'),
                    type: 'metricsEditor',
                    tooltip: T('Calculate the size of the slices (e.g. Count of IDs or Sum of Total).'),
                    sector: 'data',
                    defaultValue: null,
                    path: 'dataQuery0.aggregation.metrics',
                    config: { single: true }
                },
                aggregationFiltersEditor: {
                    name: T('Summary Filters'),
                    type: 'aggregationFiltersEditor',
                    tooltip: T('Filter based on calculated results (e.g. show groups where Count > 5).'),
                    sector: 'data',
                    defaultValue: null,
                    path: 'dataQuery0.aggregationFilter'
                },
                sortOrder: {
                    name: T('Row Limit Sort Order'),
                    type: 'sortOrderEditor',
                    tooltip: T('Determine which rows are kept when a Limit is applied (e.g. Top 10 by Date).'),
                    sector: 'data',
                    defaultValue: null,
                    path: 'dataQuery0.sortOrders'
                },
                limit: {
                    name: T('Row Limit'),
                    type: 'numeric',
                    tooltip: T('Maximum number of slices to display.'),
                    defaultValue: 100,
                    path: 'dataQuery0.limit',
                    config: { min: 1, max: 100 },
                    sector: 'data'
                },
                offset: {
                    name: T('Row Offset'),
                    type: 'numeric',
                    tooltip: T('Skip the first N rows (useful for pagination).'),
                    defaultValue: 0,
                    path: 'dataQuery0.offset',
                    config: { min: 0 },
                    sector: 'data'
                },

                // --- Layout & Styling ---
                displaySortOrder: {
                    name: T('Display Sort Order'),
                    type: 'sortOrderEditor',
                    tooltip: T('Control the visual order of the slices (e.g. Largest to Smallest).'),
                    sector: 'layout',
                    defaultValue: null,
                    config: { dataQuery: 'dataQuery0' },
                    path: 'layout.displaySortOrders'
                },

                ...COLOR_PROPERTIES,

                chartOptionsGroup: {
                    name: T('Chart Options'),
                    type: 'group',
                    sector: 'layout',
                    defaultValue: null,
                    tooltip: ''
                },
                chartStyle: {
                    name: T('Chart Style'),
                    type: 'enum',
                    tooltip: T('Switch between standard Pie or Doughnut style.'),
                    defaultValue: 'Pie',
                    options: ['Pie', 'Doughnut'],
                    sector: 'layout',
                    path: 'layout.chartStyle'
                },
                roseType: {
                    name: T('Rose Type'),
                    type: 'enum',
                    tooltip: T('Use Nightingale Rose mode where slice radius varies by value.'),
                    defaultValue: 'None',
                    options: ['None', 'Area', 'Radius'],
                    sector: 'layout',
                    path: 'layout.roseType'
                },

                enableCustomGeometry: {
                    name: T('Customize Geometry'),
                    type: 'bool',
                    tooltip: T('Manually adjust the size and position of the pie/doughnut.'),
                    defaultValue: false,
                    sector: 'layout',
                    path: 'layout.enableCustomGeometry'
                },
                pieOuterRadius: {
                    name: T('Radius (%)'),
                    type: 'numeric',
                    sector: 'layout',
                    defaultValue: 75,
                    config: { appearance: 'slider', min: 10, max: 100 },
                    tooltip: T('Outer size of the chart.'),
                    path: 'layout.pieOuterRadius',
                    visibleWhen: (c) => asDash(c).layout?.enableCustomGeometry === true
                },
                pieInnerRadius: {
                    name: T('Inner Radius (%)'),
                    type: 'numeric',
                    sector: 'layout',
                    defaultValue: 45,
                    config: { appearance: 'slider', min: 0, max: 90 },
                    tooltip: T('Size of the hollow center (Doughnut only).'),
                    path: 'layout.pieInnerRadius',
                    visibleWhen: (c) => asDash(c).layout?.chartStyle === 'Doughnut' && asDash(c).layout?.enableCustomGeometry === true
                },
                pieCenterX: {
                    name: T('Center X (%)'),
                    type: 'numeric',
                    sector: 'layout',
                    defaultValue: 50,
                    config: { appearance: 'slider', min: 0, max: 100 },
                    tooltip: T('Horizontal position (0% = left, 100% = right).'),
                    path: 'layout.pieCenterX',
                    visibleWhen: (c) => asDash(c).layout?.enableCustomGeometry === true
                },
                pieCenterY: {
                    name: T('Center Y (%)'),
                    type: 'numeric',
                    sector: 'layout',
                    defaultValue: 50,
                    config: { appearance: 'slider', min: 0, max: 100 },
                    tooltip: T('Vertical position (0% = top, 100% = bottom).'),
                    path: 'layout.pieCenterY',
                    visibleWhen: (c) => asDash(c).layout?.enableCustomGeometry === true
                },

                ...LEGEND_PROPERTIES,
                ...DATA_LABEL_PROPERTIES,

                tooltipGroup: {
                    name: T('Tooltip'),
                    type: 'group',
                    sector: 'layout',
                    defaultValue: null,
                    tooltip: ''
                },
                tooltipTrigger: {
                    name: T('Tooltip Trigger'),
                    type: 'enum',
                    options: ['Item', 'None'],
                    sector: 'layout',
                    defaultValue: 'Item',
                    tooltip: T('Choose when the info popup appears.'),
                    path: 'styling.tooltipTrigger'
                },
                ...TOOLTIP_STYLE_PROPERTIES
            },
            toggleEditors: toggleEditorsFn,
            render: chartFn
        },
        'lineChart': {
            title: T('Line Chart'), description: T('Display data as a line chart.'), icon: 'fa-chart-line', category: 'Charts',
            properties: {
                ...BASE_PROPERTIES,

                datasetSelector: {
                    name: T('Datasets'),
                    type: 'datasetSelector',
                    tooltip: T('Select the datasets(s) to use for this component.'),
                    sector: 'data',
                    defaultValue: null,
                    path: 'dataQuery0'
                },
                ...GLOBAL_FILTER_BINDINGS_PROPERTY,
                filtersEditor: {
                    name: T('Filters'),
                    type: 'filtersEditor',
                    tooltip: T('Restrict data based on specific criteria.'),
                    sector: 'data',
                    defaultValue: null,
                    path: 'dataQuery0.filter'
                },
                groupBy: {
                    name: T('Group By'),
                    type: 'columnSelector',
                    tooltip: T('The X-Axis category (e.g. Date or Status).'),
                    sector: 'data',
                    defaultValue: null,
                    path: 'dataQuery0.aggregation.groupBy'
                },
                metrics: {
                    name: T('Metrics'),
                    type: 'metricsEditor',
                    tooltip: T('The Y-Axis values (e.g. Sum of Sales).'),
                    sector: 'data',
                    defaultValue: null,
                    path: 'dataQuery0.aggregation.metrics'
                },
                breakDownBy: {
                    name: T('Break Down By'),
                    type: 'columnSelector',
                    tooltip: T('Create multiple lines based on a category (e.g. Sales by Date, broken down by Region).'),
                    sector: 'data',
                    defaultValue: null,
                    path: 'dataQuery0.aggregation.breakDownBy'
                },
                breakDownByLimit: {
                    name: T('Break Down Limit'),
                    type: 'breakDownByLimitEditor',
                    tooltip: T('Limit how many breakdown lines are shown (e.g. Top 5 Regions).'),
                    sector: 'data',
                    defaultValue: null,
                    path: 'dataQuery0.aggregation.breakDownByLimit'
                },
                aggregationFiltersEditor: {
                    name: T('Summary Filters'),
                    type: 'aggregationFiltersEditor',
                    tooltip: T('Filter based on calculated results.'),
                    sector: 'data',
                    defaultValue: null,
                    path: 'dataQuery0.aggregationFilter'
                },
                sortOrder: {
                    name: T('Limit Sort Order'),
                    type: 'sortOrderEditor',
                    tooltip: T('Determine which rows are kept when a Row Limit is applied.'),
                    sector: 'data',
                    defaultValue: null,
                    path: 'dataQuery0.sortOrders'
                },
                limit: {
                    name: T('Row Limit'),
                    type: 'numeric',
                    tooltip: T('Maximum number of X-Axis points.'),
                    defaultValue: 500,
                    path: 'dataQuery0.limit',
                    config: { min: 1, max: 500 },
                    sector: 'data'
                },
                offset: {
                    name: T('Row Offset'),
                    type: 'numeric',
                    tooltip: T('Skip the first N rows.'),
                    defaultValue: 0,
                    path: 'dataQuery0.offset',
                    config: { min: 0 },
                    sector: 'data'
                },

                displaySortOrder: {
                    name: T('X-Axis Sort Order'),
                    type: 'sortOrderEditor',
                    tooltip: T('Control the visual order of the X-Axis (e.g. Chronological vs Value based).'),
                    sector: 'layout',
                    defaultValue: null,
                    path: 'layout.displaySortOrders',
                    config: { dataQuery: 'dataQuery0' }
                },
                referenceLines: {
                    name: T('Reference Lines'),
                    type: 'referenceLinesEditor',
                    sector: 'layout',
                    defaultValue: [],
                    tooltip: T('Add static or calculated threshold lines.'),
                    path: 'layout.referenceLines'
                },
                ...COLOR_PROPERTIES,

                chartOptionsGroup: {
                    name: T('Chart Layout'),
                    type: 'group',
                    sector: 'layout',
                    defaultValue: null,
                    tooltip: ''
                },
                chartStyle: {
                    name: T('Fill Style'),
                    type: 'enum',
                    tooltip: T('Switch between standard Line or filled Area chart.'),
                    defaultValue: 'Line',
                    options: ['Line', 'Area'],
                    sector: 'layout',
                    path: 'layout.chartStyle'
                },
                markerStyle: {
                    name: T('Markers'),
                    type: 'enum',
                    defaultValue: 'Cycle',
                    options: ['Cycle', 'Circle', 'None'],
                    tooltip: T('Cycle: Different shapes per series.\nNone: Show only on hover.'),
                    sector: 'layout',
                    path: 'layout.markerStyle'
                },
                stackingMode: {
                    name: T('Stacking Mode'),
                    type: 'enum',
                    tooltip: T('Stack series on top of each other to show cumulative totals.'),
                    defaultValue: 'None',
                    options: ['None', 'Normal'],
                    sector: 'layout',
                    path: 'layout.stackingMode'
                },
                lineSmooth: {
                    name: T('Smooth Lines'),
                    type: 'bool',
                    defaultValue: false,
                    sector: 'layout',
                    tooltip: T('Render curves (spline) instead of straight lines.'),
                    path: 'layout.lineSmooth'
                },
                connectNulls: {
                    name: T('Connect Missing Data'),
                    type: 'bool',
                    tooltip: T('Bridge gaps in the line caused by missing data points.'),
                    defaultValue: true,
                    sector: 'layout',
                    path: 'layout.connectNulls'
                },

                ...CARTESIAN_GEOMETRY_PROPERTIES,
                lineWidth: {
                    name: T('Line Width'),
                    type: 'numeric',
                    defaultValue: 2,
                    config: { min: 1, max: 10, appearance: 'slider' },
                    sector: 'layout',
                    tooltip: T('Thickness of the chart lines.'),
                    path: 'layout.lineWidth',
                    visibleWhen: (c) => asDash(c).layout?.enableCustomGeometry === true
                },

                xAxisGroup: {
                    name: T('X Axis'),
                    type: 'group',
                    sector: 'layout',
                    defaultValue: null,
                    tooltip: ''
                },
                xAxisLabelRotation: {
                    name: T('Label Rotation'),
                    type: 'numeric',
                    tooltip: T('Rotate X-axis labels to prevent overlap.'),
                    defaultValue: 30,
                    sector: 'layout',
                    config: { appearance: 'slider', min: 0, max: 180, step: 1 },
                    path: 'styling.xAxisLabelRotation',
                },
                ...createFormattingProperties({
                    groupPrefix: 'xAxis',
                    displayPrefix: T('X Axis'),
                    pathRoot: 'styling.xAxisValueFormatting',
                    visibleWhen: (c) => true,
                    defaultFormat: 'Category',
                    defaultStyle: { color: '#666666', size: 'Small', decor: 'Normal' },
                    exclude: ['Format']
                }),

                yAxisGroup: {
                    name: T('Y Axis'),
                    type: 'group',
                    sector: 'layout',
                    defaultValue: null,
                    tooltip: ''
                },
                yAxisLeftTitle: {
                    name: T('Y Axis Title'),
                    type: 'text',
                    sector: 'layout',
                    defaultValue: '',
                    tooltip: T('Label displayed alongside the Y Axis.'),
                    path: 'styling.yAxisLeftTitle'
                },
                yAxisGridLines: {
                    name: T('Y Axis Grid Lines'),
                    type: 'enum',
                    options: ['None', 'Horizontal'],
                    sector: 'layout',
                    defaultValue: 'Horizontal',
                    tooltip: T('Show background grid lines for better readability.'),
                    path: 'styling.yAxisGridLines'
                },
                yAxisScale: {
                    name: T('Y Axis Scale'),
                    type: 'enum',
                    tooltip: T('Use Logarithmic for data with massive variations in scale.'),
                    defaultValue: 'Linear',
                    options: ['Linear', 'Logarithmic'],
                    sector: 'layout',
                    path: 'styling.yAxisScale'
                },
                yAxisMin: {
                    name: T('Min Value'),
                    type: 'numeric',
                    sector: 'layout',
                    defaultValue: null,
                    tooltip: T('Force the axis to start at this value. Leave empty for auto calculation.'),
                    path: 'layout.yAxisMin'
                },
                yAxisMax: {
                    name: T('Max Value'),
                    type: 'numeric',
                    sector: 'layout',
                    defaultValue: null,
                    tooltip: T('Force the axis to end at this value. Leave empty for auto calculation.'),
                    path: 'layout.yAxisMax'
                },
                ...createFormattingProperties({
                    groupPrefix: 'yAxis',
                    displayPrefix: T('Y Axis'),
                    pathRoot: 'styling.yAxisValueFormatting',
                    visibleWhen: (c) => true,
                    defaultFormat: 'Value',
                    defaultStyle: { color: '#666666', size: 'Small', decor: 'Normal' },
                    exclude: ['Format']
                }),

                ...LEGEND_PROPERTIES,
                ...DATA_LABEL_PROPERTIES,

                tooltipGroup: {
                    name: T('Tooltip'),
                    type: 'group',
                    sector: 'layout',
                    defaultValue: null,
                    tooltip: ''
                },
                tooltipTrigger: {
                    name: T('Tooltip Trigger'),
                    type: 'enum',
                    options: ['Axis', 'Item', 'None'],
                    sector: 'layout',
                    defaultValue: 'Axis',
                    tooltip: T('Axis: Show all series values for the hovered X point.\nItem: Show only the hovered series value.'),
                    path: 'styling.tooltipTrigger'
                },
                ...TOOLTIP_STYLE_PROPERTIES,
            },
            toggleEditors: toggleEditorsFn,
            render: chartFn
        },
        'barChart': {
            title: T('Bar Chart'), description: T('Display data as a bar chart.'), icon: 'fa-chart-bar', category: 'Charts',
            properties: {
                ...BASE_PROPERTIES,

                // --- Data ---
                datasetSelector: {
                    name: T('Datasets'),
                    type: 'datasetSelector',
                    tooltip: T('Select the datasets(s) to use for this component.'),
                    sector: 'data',
                    defaultValue: null,
                    path: 'dataQuery0'
                },
                ...GLOBAL_FILTER_BINDINGS_PROPERTY,
                filtersEditor: {
                    name: T('Filters'),
                    type: 'filtersEditor',
                    tooltip: T('Restrict data based on specific criteria.'),
                    sector: 'data',
                    defaultValue: null,
                    path: 'dataQuery0.filter'
                },
                groupBy: {
                    name: T('Group By'),
                    type: 'columnSelector',
                    tooltip: T('The category for the bars (e.g. Date or Status).'),
                    sector: 'data',
                    defaultValue: null,
                    path: 'dataQuery0.aggregation.groupBy'
                },
                metrics: {
                    name: T('Metrics'),
                    type: 'metricsEditor',
                    tooltip: T('The value determines bar height/length.'),
                    sector: 'data',
                    defaultValue: null,
                    path: 'dataQuery0.aggregation.metrics'
                },
                breakDownBy: {
                    name: T('Break Down By'),
                    type: 'columnSelector',
                    tooltip: T('Split bars into segments based on a category.'),
                    sector: 'data',
                    defaultValue: null,
                    path: 'dataQuery0.aggregation.breakDownBy'
                },
                breakDownByLimit: {
                    name: T('Break Down Limit'),
                    type: 'breakDownByLimitEditor',
                    tooltip: T('Limit how many breakdown segments are shown.'),
                    sector: 'data',
                    defaultValue: null,
                    path: 'dataQuery0.aggregation.breakDownByLimit'
                },
                aggregationFiltersEditor: {
                    name: T('Summary Filters'),
                    type: 'aggregationFiltersEditor',
                    tooltip: T('Filter based on calculated results.'),
                    sector: 'data',
                    defaultValue: null,
                    path: 'dataQuery0.aggregationFilter'
                },
                sortOrder: {
                    name: T('Limit Sort Order'),
                    type: 'sortOrderEditor',
                    tooltip: T('Determine which rows are kept when a Row Limit is applied.'),
                    sector: 'data',
                    defaultValue: null,
                    path: 'dataQuery0.sortOrders'
                },
                limit: {
                    name: T('Row Limit'),
                    type: 'numeric',
                    tooltip: T('Maximum number of bars to display.'),
                    defaultValue: 500,
                    path: 'dataQuery0.limit',
                    config: { min: 1, max: 500 },
                    sector: 'data'
                },
                offset: {
                    name: T('Row Offset'),
                    type: 'numeric',
                    tooltip: T('Skip the first N rows.'),
                    defaultValue: 0,
                    path: 'dataQuery0.offset',
                    config: { min: 0 },
                    sector: 'data'
                },

                // --- Layout & Styling ---
                displaySortOrder: {
                    name: T('Display Sort Order'),
                    type: 'sortOrderEditor',
                    tooltip: T('Control the visual order of the bars.'),
                    sector: 'layout',
                    defaultValue: null,
                    path: 'layout.displaySortOrders',
                    config: { dataQuery: 'dataQuery0' }
                },
                ...COLOR_PROPERTIES,

                chartOptionsGroup: {
                    name: T('Chart Options'),
                    type: 'group',
                    sector: 'layout',
                    defaultValue: null,
                    tooltip: ''
                },
                stackingMode: {
                    name: T('Stacking Mode'),
                    type: 'enum',
                    tooltip: T('Stack bars on top of each other to show cumulative totals.'),
                    defaultValue: 'Normal',
                    options: ['None', 'Normal'],
                    sector: 'layout',
                    path: 'layout.stackingMode'
                },

                ...LEGEND_PROPERTIES,
                ...DATA_LABEL_PROPERTIES,
                ...CARTESIAN_GEOMETRY_PROPERTIES,

                // X Axis
                xAxisGroup: {
                    name: T('X Axis'),
                    type: 'group',
                    sector: 'layout',
                    defaultValue: null,
                    tooltip: ''
                },
                xAxisLabelRotation: {
                    name: T('Label Rotation'),
                    type: 'numeric',
                    tooltip: T('Rotate labels to prevent overlap.'),
                    defaultValue: 30,
                    sector: 'layout',
                    config: { appearance: 'slider', min: 0, max: 180, step: 1 },
                    path: 'styling.xAxisLabelRotation'
                },
                ...createFormattingProperties({
                    groupPrefix: 'xAxis',
                    displayPrefix: T('X Axis'),
                    pathRoot: 'styling.xAxisValueFormatting',
                    visibleWhen: (c) => true,
                    defaultFormat: 'Category',
                    defaultStyle: { color: '#666666', size: 'Small', decor: 'Normal' },
                    exclude: ['Format']
                }),

                // Y Axis
                yAxisGroup: {
                    name: T('Y Axis'),
                    type: 'group',
                    sector: 'layout',
                    defaultValue: null,
                    tooltip: ''
                },
                yAxisLeftTitle: {
                    name: T('Axis Title'),
                    type: 'text',
                    sector: 'layout',
                    defaultValue: '',
                    tooltip: T('Label displayed alongside the Y Axis.'),
                    path: 'styling.yAxisLeftTitle'
                },
                yAxisScale: {
                    name: T('Scale'),
                    type: 'enum',
                    tooltip: T('Use Logarithmic for data with massive variations in scale.'),
                    defaultValue: 'Linear',
                    options: ['Linear', 'Logarithmic'],
                    sector: 'layout',
                    path: 'styling.yAxisScale'
                },
                ...createFormattingProperties({
                    groupPrefix: 'yAxis',
                    displayPrefix: T('Y Axis'),
                    pathRoot: 'styling.yAxisValueFormatting',
                    visibleWhen: (c) => true,
                    defaultFormat: 'Value',
                    defaultStyle: { color: '#666666', size: 'Small', decor: 'Normal' },
                    exclude: ['Format']
                }),
                yAxisGridLines: {
                    name: T('Grid Lines'),
                    type: 'enum',
                    options: ['None', 'Horizontal'],
                    sector: 'layout',
                    defaultValue: 'Horizontal',
                    tooltip: T('Show background grid lines.'),
                    path: 'styling.yAxisGridLines'
                },

                // Tooltip
                tooltipGroup: {
                    name: T('Tooltip'),
                    type: 'group',
                    sector: 'layout',
                    defaultValue: null,
                    tooltip: ''
                },
                tooltipTrigger: {
                    name: T('Tooltip Trigger'),
                    type: 'enum',
                    options: ['Axis', 'Item', 'None'],
                    sector: 'layout',
                    defaultValue: 'Axis',
                    tooltip: T('Axis: Show all series values for the hovered category.\nItem: Show only the hovered bar value.'),
                    path: 'styling.tooltipTrigger'
                },
                ...TOOLTIP_STYLE_PROPERTIES
            },
            toggleEditors: toggleEditorsFn,
            render: chartFn
        },
        'heading': {
            title: T('Heading'), description: T('A customizable text heading.'), icon: 'fa-heading', category: 'Widgets',
            properties: {
                headingText: {
                    name: T('Text'),
                    type: 'textMulti',
                    defaultValue: 'New Heading',
                    sector: 'basic',
                    tooltip: T('The primary text to display.')
                },

                // Typography
                typographyGroup: {
                    name: T('Typography'),
                    type: 'group',
                    sector: 'layout',
                    defaultValue: null,
                    tooltip: ''
                },
                headingAlign: {
                    name: T('Alignment'),
                    type: 'enum',
                    defaultValue: 'Left',
                    options: ['Left', 'Center', 'Right'],
                    sector: 'layout',
                    tooltip: T('Horizontal alignment of the text.'),
                    path: 'layout.headingAlign'
                },
                headingSize: {
                    name: T('Font Size'),
                    type: 'numeric',
                    defaultValue: 24,
                    sector: 'layout',
                    config: { min: 8, max: 120 },
                    tooltip: T('Size of the text.'),
                    path: 'styling.headingSize'
                },
                headingColor: {
                    name: T('Text Color'),
                    type: 'colorSelector',
                    defaultValue: '#2c3e50',
                    sector: 'layout',
                    tooltip: T('Color of the text.'),
                    path: 'styling.headingColor'
                },
                isBold: {
                    name: T('Bold'),
                    type: 'bool',
                    defaultValue: true,
                    sector: 'layout',
                    tooltip: T('Apply bold weight.'),
                    path: 'styling.isBold'
                },
                isItalic: {
                    name: T('Italic'),
                    type: 'bool',
                    defaultValue: false,
                    sector: 'layout',
                    tooltip: T('Apply italic style.'),
                    path: 'styling.isItalic'
                },

                bgColor: {
                    name: T('Background'),
                    type: 'colorSelector',
                    defaultValue: 'transparent',
                    sector: 'layout',
                    tooltip: T('Background color of the component container.')
                },
            },
            render: widgetFn
        },
        'table': {
            title: T('Table'), description: T('A list or table of items.'), icon: 'fa-table', category: 'Widgets',
            properties: {
                ...BASE_PROPERTIES,

                // --- Data ---
                datasetSelector: {
                    name: T('Datasets'),
                    type: 'datasetSelector',
                    tooltip: T('Select the dataset(s) to use for this component.'),
                    sector: 'data',
                    defaultValue: null,
                    path: 'dataQuery0'
                },
                ...GLOBAL_FILTER_BINDINGS_PROPERTY,
                filtersEditor: {
                    name: T('Filters'),
                    type: 'filtersEditor',
                    tooltip: T('Restrict data based on specific criteria.'),
                    sector: 'data',
                    defaultValue: null,
                    path: 'dataQuery0.filter'
                },
                projection: {
                    name: T('Columns'),
                    type: 'projectionSelector',
                    tooltip: T('Choose which specific columns to display in the grid.'),
                    sector: 'data',
                    defaultValue: null,
                    path: 'dataQuery0.projection'
                },
                groupBy: {
                    name: T('Group By'),
                    type: 'columnSelector',
                    tooltip: T('Group identical rows together based on this column.'),
                    sector: 'data',
                    defaultValue: null,
                    path: 'dataQuery0.aggregation.groupBy',
                    config: { allowAlias: true}
                },
                metrics: {
                    name: T('Metrics'),
                    type: 'metricsEditor',
                    tooltip: T('Add calculated columns (e.g. Counts or Sums).'),
                    sector: 'data',
                    defaultValue: null,
                    path: 'dataQuery0.aggregation.metrics'
                },
                breakDownBy: {
                    name: T('Break Down By'),
                    type: 'columnSelector',
                    tooltip: T('Pivot the table: Create new columns for each value in this category.'),
                    sector: 'data',
                    defaultValue: null,
                    path: 'dataQuery0.aggregation.breakDownBy',
                    config: { allowAlias: true}
                },
                breakDownByLimit: {
                    name: T('Break Down Limit'),
                    type: 'breakDownByLimitEditor',
                    tooltip: T('Limit the number of pivot columns created.'),
                    sector: 'data',
                    defaultValue: null,
                    path: 'dataQuery0.aggregation.breakDownByLimit'
                },
                aggregationFiltersEditor: {
                    name: T('Summary Filters'),
                    type: 'aggregationFiltersEditor',
                    tooltip: T('Filter based on calculated results.'),
                    sector: 'data',
                    defaultValue: null,
                    path: 'dataQuery0.aggregationFilter'
                },
                sortOrder: {
                    name: T('Row Sort Order'),
                    type: 'sortOrderEditor',
                    tooltip: T('Determine the order of rows (e.g. Newest first).'),
                    sector: 'data',
                    defaultValue: null,
                    path: 'dataQuery0.sortOrders'
                },
                limit: {
                    name: T('Row Limit'),
                    type: 'numeric',
                    tooltip: T('Maximum number of rows to retrieve. Leave blank for no limit.'),
                    defaultValue: null,
                    path: 'dataQuery0.limit',
                    config: { min: 1 },
                    sector: 'data'
                },
                offset: {
                    name: T('Row Offset'),
                    type: 'numeric',
                    tooltip: T('Skip the first N rows.'),
                    defaultValue: 0,
                    path: 'dataQuery0.offset',
                    config: { min: 0 },
                    sector: 'data'
                },

                // --- Layout & Styling ---
                optionsGroup: {
                    name: T('Table Options'),
                    type: 'group',
                    sector: 'layout',
                    defaultValue: null,
                    tooltip: ''
                },
                pageSize: {
                    name: T('Page Size'),
                    type: 'enum',
                    tooltip: T('Rows per page before pagination controls appear.'),
                    defaultValue: 5,
                    options: [5, 10, 25],
                    sector: 'layout',
                    path: 'layout.pageSize'
                },
                bgColor: {
                    name: T('Background Color'),
                    type: 'colorSelector',
                    tooltip: T('Fill color for the table background.'),
                    defaultValue: 'transparent',
                    sector: 'layout'
                },
            },
            toggleEditors: toggleEditorsFn,
            render: widgetFn
        },
        'valueLabel': {
            title: T('Metric'), description: T('Display a key metric with trends, targets, and alert states.'), icon: 'fa-tag', category: 'Widgets',
            properties: {
                ...BASE_PROPERTIES,

                // --- Data ---
                datasetSelector: {
                    name: T('Datasets'),
                    type: 'datasetSelector',
                    tooltip: T('Select the dataset(s) to use for this component.'),
                    sector: 'data',
                    defaultValue: null,
                    path: 'dataQuery0'
                },
                ...GLOBAL_FILTER_BINDINGS_PROPERTY,
                filtersEditor: {
                    name: T('Filters'),
                    type: 'filtersEditor',
                    tooltip: T('Filter data.'),
                    sector: 'data',
                    defaultValue: null,
                    path: 'dataQuery0.filter'
                },
                groupBy: {
                    name: T('Group By'),
                    type: 'columnSelector',
                    tooltip: T('Select a time or sequence column to enable trend calculation and sparklines.'),
                    sector: 'data',
                    defaultValue: null,
                    path: 'dataQuery0.aggregation.groupBy',
                    config: { allowedTypes: ['Date', 'Number', 'Text'] }
                },
                metrics: {
                    name: T('Metric'),
                    type: 'metricsEditor',
                    tooltip: T('The main value to display.'),
                    sector: 'data',
                    defaultValue: null,
                    path: 'dataQuery0.aggregation.metrics',
                    config: { single: true }
                },
                summaryMode: {
                    name: T('Display Mode'),
                    type: 'enum',
                    tooltip: T('Latest Value: Shows the most recent record.\nPeriod Comparison: Calculates difference between two time periods (e.g. This Month vs Last Month).'),
                    sector: 'data',
                    defaultValue: 'Latest Value',
                    options: ['Latest Value', 'Period Comparison'],
                    path: 'layout.summaryMode'
                },
                sortOrder: {
                    name: T('Sort Order'),
                    type: 'sortOrderEditor',
                    tooltip: T('Sort DESCENDING to ensure the most recent data is used.'),
                    sector: 'data',
                    defaultValue: [{ type: 'Desc', column: { datasetIndex: 0, columnId: '' } }],
                    path: 'dataQuery0.sortOrders'
                },
                limit: {
                    name: T('Row Limit'),
                    type: 'numeric',
                    tooltip: T('Latest Value: Load enough rows for the sparkline (e.g. 20).\nPeriod Comparison: Set total window size (e.g. 60 to compare Last 30 vs Prior 30).'),
                    defaultValue: 500,
                    path: 'dataQuery0.limit',
                    config: { min: 2, max: 500 },
                    sector: 'data'
                },
                targetValue: {
                    name: T('Goal / Target'),
                    type: 'numeric',
                    tooltip: T('Set a static target value to enable progress bars and percentage-of-target alerts.'),
                    sector: 'data',
                    defaultValue: null
                },

                // --- Layout & Styling ---
                cardGroup: {
                    name: T('Card'),
                    type: 'group',
                    sector: 'layout',
                    defaultValue: null,
                    tooltip: ''
                },
                kpiStyle: {
                    name: T('Chart Style'),
                    type: 'enum',
                    tooltip: T('Basic: Just value and trend.\nSparkline: Adds a mini-chart background.'),
                    defaultValue: 'Basic',
                    options: ['Basic', 'Sparkline'],
                    sector: 'layout',
                    path: 'layout.kpiStyle'
                },
                bgColor: {
                    name: T('Background'),
                    type: 'colorSelector',
                    tooltip: T('Card background color.'),
                    defaultValue: '#ffffff',
                    sector: 'layout'
                },

                valueGroup: {
                    name: T('Value'),
                    type: 'group',
                    sector: 'layout',
                    defaultValue: null,
                    tooltip: ''
                },
                valueColor: {
                    name: T('Text Color'),
                    type: 'colorSelector',
                    tooltip: T('Default color for the main value text.'),
                    defaultValue: '#2c3e50',
                    sector: 'layout',
                    path: 'styling.valueColor'
                },
                valuePrefix: {
                    name: T('Value Prefix'),
                    type: 'text',
                    tooltip: T('Symbol before the value (e.g. $).'),
                    defaultValue: '',
                    sector: 'layout',
                    config: { placeholder: '$'},
                    path: 'styling.valuePrefix'
                },
                valueSuffix: {
                    name: T('Value Suffix'),
                    type: 'text',
                    tooltip: T('Symbol after the value (e.g. USD or %).'),
                    defaultValue: '',
                    sector: 'layout',
                    config: { placeholder: ' USD'},
                    path: 'styling.valueSuffix'
                },
                valueDecimals: {
                    name: T('Decimals'),
                    type: 'numeric',
                    tooltip: T('Fixed number of decimal places to display.'),
                    defaultValue: null,
                    sector: 'layout',
                    config: { min: 0, max: 10 },
                    path: 'styling.valueDecimals'
                },

                trendGroup: {
                    name: T('Trend'),
                    type: 'group',
                    sector: 'layout',
                    defaultValue: null,
                    tooltip: ''
                },
                showTrend: {
                    name: T('Show Trend'),
                    type: 'bool',
                    tooltip: T('Display the change vs the previous period.'),
                    defaultValue: true,
                    sector: 'layout',
                    path: 'layout.showTrend'
                },
                trendDisplayMode: {
                    name: T('Trend Display'),
                    type: 'enum',
                    tooltip: T('Choose how to display the trend (Percentage, Absolute diff, or Both).'),
                    defaultValue: 'Percent',
                    options: ['Percent', 'Absolute', 'Both'],
                    sector: 'layout',
                    path: 'layout.trendDisplayMode'
                },
                trendColorMode: {
                    name: T('Trend Logic'),
                    type: 'enum',
                    tooltip: T('Standard: Green = Up.\nInverted: Green = Down (good for costs/errors).'),
                    defaultValue: 'Standard',
                    options: ['Standard', 'Inverted'],
                    sector: 'layout',
                    path: 'styling.trendColorMode'
                },
                sparklineColor: {
                    name: T('Sparkline Color'),
                    type: 'colorSelector',
                    tooltip: T('Color of the mini-chart background.'),
                    defaultValue: '#e0e7ff',
                    sector: 'layout',
                    path: 'styling.sparklineColor'
                },

                progressGroup: {
                    name: T('Progress Bar'),
                    type: 'group',
                    sector: 'layout',
                    defaultValue: null,
                    tooltip: ''
                },
                showProgressBar: {
                    name: T('Show Progress Bar'),
                    type: 'bool',
                    tooltip: T('Display a progress bar relative to the Goal/Target value.'),
                    defaultValue: false,
                    sector: 'layout',
                    path: 'layout.showProgressBar'
                },

                alertsGroup: {
                    name: T('Conditional Formatting'),
                    type: 'group',
                    sector: 'layout',
                    defaultValue: null,
                    tooltip: ''
                },
                enableThresholds: {
                    name: T('Enable Alerts'),
                    type: 'bool',
                    tooltip: T('Change colors when value hits warning/danger levels.'),
                    defaultValue: false,
                    sector: 'layout',
                    path: 'styling.enableThresholds'
                },
                thresholdType: {
                    name: T('Rule Base'),
                    type: 'enum',
                    defaultValue: 'Absolute',
                    sector: 'layout',
                    tooltip: T('Compare against the raw value (Absolute) or the percentage of the Goal (Percent of Target).'),
                    options: ['Absolute', 'Percent of Target'],
                    path: 'styling.thresholdType',
                    visibleWhen: (c) => asDash(c).styling?.enableThresholds === true
                },
                thresholdLogic: {
                    name: T('Alert Logic'),
                    type: 'enum',
                    defaultValue: 'Standard',
                    options: ['Standard', 'Inverted'],
                    tooltip: T('Standard: High values are good.\nInverted: Low values are good.'),
                    sector: 'layout',
                    path: 'styling.thresholdLogic',
                    visibleWhen: (c) => asDash(c).styling?.enableThresholds === true
                },
                warningThreshold: {
                    name: T('Warning Level'),
                    type: 'numeric',
                    sector: 'layout',
                    defaultValue: null,
                    tooltip: T('Threshold for Warning state (Orange).'),
                    path: 'styling.warningThreshold',
                    visibleWhen: (c) => asDash(c).styling?.enableThresholds === true
                },
                dangerThreshold: {
                    name: T('Danger Level'),
                    type: 'numeric',
                    sector: 'layout',
                    defaultValue: null,
                    tooltip: T('Threshold for Critical state (Red).'),
                    path: 'styling.dangerThreshold',
                    visibleWhen: (c) => asDash(c).styling?.enableThresholds === true
                },
                applyConditionTo: {
                    name: T('Apply Color To'),
                    type: 'enum',
                    defaultValue: 'Text',
                    options: ['Text', 'Card', 'Value'],
                    sector: 'layout',
                    tooltip: T('Controls which part of the widget changes color.'),
                    path: 'styling.applyConditionTo',
                    visibleWhen: (c) => asDash(c).styling?.enableThresholds === true
                },
            },
            toggleEditors: toggleEditorsFn,
            render: widgetFn
        },
        'distillQuery': {
            title: T('Ask'), description: T('An interactive chat widget powered by your Artifacts.'), icon: 'fa-comments', category: 'Widgets',
            properties: {
                ...BASE_PROPERTIES,
                systemInstruction: {
                    name: T('System Instruction'),
                    type: 'textMulti',
                    tooltip: T(`Hidden context for the AI (e.g., 'You are a financial analyst').`),
                    defaultValue: '',
                    sector: 'basic'
                },
                scopeItems: {
                    name: T('Scope'),
                    type: 'scopeItemsEditor',
                    tooltip: T('Restrict the search to specific folders and artifacts.'),
                    defaultValue: [],
                    sector: 'basic'
                },
                welcomeMessage: {
                    name: T('Welcome Message'),
                    type: 'textMulti',
                    tooltip: T('The initial greeting message displayed to the user.'),
                    defaultValue: 'Hello!\nHow can I help you?',
                    sector: 'basic'
                },

                // Layout & Styling
                bgColor: {
                    name: T('Background Color'),
                    type: 'colorSelector',
                    tooltip: T('Background color of the component container.'),
                    defaultValue: 'transparent',
                    sector: 'layout'
                },
                headerBgColor: {
                    name: T('Header Background'),
                    type: 'colorSelector',
                    tooltip: T('Background color for the title bar.'),
                    defaultValue: 'transparent',
                    sector: 'layout',
                    path: 'styling.headerBgColor'
                },
                inputBgColor: {
                    name: T('Input Background'),
                    type: 'colorSelector',
                    tooltip: T('Background color for the text input box.'),
                    defaultValue: 'transparent',
                    sector: 'layout',
                    path: 'styling.inputBgColor'
                },
                
                buttonsGroup: {
                    name: T('Chat Interface'),
                    type: 'group',
                    sector: 'layout',
                    defaultValue: null,
                    tooltip: ''
                },
                sendButtonTextColor: {
                    name: T('Button Text Color'),
                    type: 'colorSelector',
                    tooltip: T('Color for the send button icon.'),
                    defaultValue: '#f7f4fc',
                    sector: 'layout',
                    path: 'styling.sendButtonTextColor'
                },
                sendButtonStyle: {
                    name: T('Button Color'),
                    type: 'colorSelector',
                    tooltip: T('Background color for the send button.'),
                    defaultValue: '#673ab7',
                    sector: 'layout',
                    path: 'styling.sendButtonStyle'
                },

                messagesGroup: {
                    name: T('Messages'),
                    type: 'group',
                    sector: 'layout',
                    defaultValue: null,
                    tooltip: ''
                },
                sentMessageTextColor: {
                    name: T('Sent Message Color'),
                    type: 'colorSelector',
                    tooltip: T('Text color for user messages.'),
                    defaultValue: '#212529',
                    sector: 'layout',
                    path: 'styling.sentMessageTextColor'
                },
                sentMessageBgColor: {
                    name: T('Sent Message Color'),
                    type: 'colorSelector',
                    tooltip: T('Background color for user messages.'),
                    defaultValue: '#f7f4fc',
                    sector: 'layout',
                    path: 'styling.sentMessageBgColor'
                },
                responseMessageTextColor: {
                    name: T('Response Text Color'),
                    type: 'colorSelector',
                    tooltip: T('Text color for AI responses.'),
                    defaultValue: '#212529',
                    sector: 'layout',
                    path: 'styling.responseMessageTextColor'
                },
                responseMessageBgColor: {
                    name: T('Response Message Color'),
                    type: 'colorSelector',
                    tooltip: T('Background color for AI responses.'),
                    defaultValue: '#f7f4fc',
                    sector: 'layout',
                    path: 'styling.responseMessageBgColor'
                },
            },
            toggleEditors: toggleEditorsFn,
            render: widgetFn
        },
        'webView': {
            title: T('Web View'), description: T('Embed a web page.'), icon: 'fa-globe', category: 'Widgets',
            properties: {
                url: {
                    name: T('URL'),
                    type: 'text',
                    tooltip: T('The full URL address to display.'),
                    defaultValue: 'https://www.google.com',
                    sector: 'basic'
                }
            }
        },
        'embeddedDash': {
            title: T('Embedded Dashboard'), description: T('Embed another dashboard.'), icon: 'fa-anchor', category: 'Widgets',
            properties: {
                dashboardId: {
                    name: T('Dashboard'),
                    type: 'enum',
                    tooltip: T('Select a dashboard to embed inside this one.'),
                    defaultValue: '',
                    options: [],
                    sector: 'basic'
                }
            }
        }
    });

    function titleBarFn(element: JQuery, component: IDashboardComponent) {
        const titleText = (component.title && component.title['eng']) ? component.title['eng'] : '';
        const $title = element.find('.componentTitle');
        $title.text(titleText);

        if (component.bgColor)
            element.css('background-color', component.bgColor);

        // Typography + alignment mirror the heading component (the titlebar centres by default).
        // These properties are new, and loaded designs aren't merged with property defaults, so
        // an unset value must fall back to the default here - notably Bold, which defaults on.
        // The font size is also exposed as a variable: the filter toggle scales off it.
        const fontSize = component.styling?.headingSize || 24;
        element.css('--titlebar-font-size', fontSize + 'px');

        element.find('.titleBarItem').css('justify-content', widgets.getFlexAlign(component.layout?.headingAlign || 'Center'));
        $title.css({
            'color': component.styling?.headingColor || '#2c3e50',
            'font-size': fontSize + 'px',
            'font-weight': (component.styling?.isBold ?? true) ? 'bold' : 'normal',
            'font-style': component.styling?.isItalic ? 'italic' : 'normal'
        });

        const hideTitle = component.hideTitle !== false;
        const isDesigner = $('body').hasClass('designerv2');

        // The filter toggle is positioned by globalFilterPane in the public view; the designer
        // renders a non-functional preview of it. The design is read directly (not the pane's
        // init state) because the canvas renders before the pane initialises.
        const design = designer._design as IDashboardDesign;
        const hasGlobalFilters = !!(design && design.globalFilters && design.globalFilters.length);

        // The filter toggle sits beside the titlebar card ([Title] [F]); the card shrinks on
        // the right to make room (see .has-filter-toggle). The designer's preview button is
        // always shown, so its card always reserves the slot.
        element.toggleClass('has-filter-toggle', isDesigner || hasGlobalFilters);

        if (isDesigner) {
            // Non-functional preview of the public view's filter toggle, rendered outside the
            // titlebar card so it stays visible when the title is hidden. Always shown — faded
            // with no global filters; clicking it selects the titlebar component so the global
            // filter properties show.
            element.find('.global-filter-toggle-preview').remove();
            // Primary once filters are defined — the faded outline button is just a placeholder.
            const $preview = $('<button type="button" tabindex="-1" class="dashboard-global-filter-toggle global-filter-toggle-preview btn btn-sm '
                + (hasGlobalFilters ? 'btn-primary' : 'btn-outline-secondary') + '">'
                + '<i class="fas fa-filter"></i></button>')
                .css('opacity', hasGlobalFilters ? '1' : '0.3');
            $preview.on('click', (e) => {
                e.stopPropagation();
                e.preventDefault();
                // Mirror the workspace click handler: mark the component selected in the
                // canvas (chrome) as well as selecting it for the property pane.
                $('.component.selected').removeClass('selected');
                element.addClass('selected');
                designer.setSelectedComponent(component.id);
            });
            element.append($preview);

            element.show();
            element.css('opacity', '1');
            $title.css('opacity', hideTitle ? '0.4' : '1');
        } else {
            // The toggle is part of the titlebar's rendering: the standalone view button is
            // adopted beside the card ([Title] [F], positioned by CSS), keeping its id and
            // bindings. Hide Title keeps the item with visibility:hidden — it still reserves
            // its grid row while only the button stays visible.
            const $toggle = $('#dashboard-global-filter-toggle');
            if (hasGlobalFilters && $toggle.length) {
                $toggle.appendTo(element);

                if (hideTitle) {
                    element.css('visibility', 'hidden');
                    $toggle.css('visibility', 'visible');
                } else {
                    element.css({ visibility: '', opacity: '1' });
                }
            } else if (hideTitle) {
                element.hide();
            } else {
                element.show();
                element.css('opacity', '1');
            }
        }
    }

    function chartFn(element: JQuery, component: IComponent) {
        charts.render(element, component);
    }

    function widgetFn(element: JQuery, component: IComponent) {
        widgets.render(element, component);
    }

    function toggleEditorsFn(component: IComponent, editors: { [propName: string]: EditorBase }): void {
        if (component.type === ComponentType.distillQuery) {
            const scopeEditor = editors['scope'];
            const recursiveScopeEditor = editors['recursiveScope'];
            if (scopeEditor && recursiveScopeEditor) {
                const $select = (scopeEditor as any).$editor.find('.item-selector');
                const selectedOptions = $select.find('option:selected');

                let hasFolder = false;
                selectedOptions.each((i, el) => {
                    if ($(el).attr('data-is-doc') === 'false') {
                        hasFolder = true;
                        return false;
                    }
                });

                recursiveScopeEditor.setVisibility(hasFolder);
            }
        }

        // DataQuery editors
        const dataQueryPropertyNames = Object.values(editors)
            .filter(editor => editor.propDef.type === 'datasetSelector' && editor.propDef.path)
            .map(editor => editor.propDef.path!.split('.')[0]);

        if (dataQueryPropertyNames.length > 0){
            const editorsByDataQuery = new Map<string, EditorBase[]>();
            for (const dataQueryName of dataQueryPropertyNames)
                editorsByDataQuery.set(dataQueryName, []);

            for (const editor of Object.values(editors)) {
                if (editor.propDef.path) {
                    const rootPath = editor.propDef.path.split('.')[0];
                    if (editorsByDataQuery.has(rootPath))
                        editorsByDataQuery.get(rootPath)!.push(editor);
                }
                // Special handling for displaySortOrder which is in layout but conceptually linked to dataQuery0
                if (editor.propDef.config?.dataQuery) {
                    const linkedDataQueryProp = editor.propDef.config.dataQuery;
                    if(editorsByDataQuery.has(linkedDataQueryProp))
                        editorsByDataQuery.get(linkedDataQueryProp)!.push(editor);
                }
            }

            editorsByDataQuery.forEach((editorsInGroup, dataQueryPropName) => {
                const dataQuery = (component as any)[dataQueryPropName] as IDataQuery | undefined;
                editorsInGroup.forEach(editor => {
                    if (editor.propDef.type !== 'datasetSelector')
                        editor.setVisibility(false);
                });

                const hasDataset = !!dataQuery?.primaryDataset?.id;
                if (!hasDataset)
                    return;

                // If this data query references a dataset the user cannot access, hide all the column/filter/metric editors
                const dataQueryRestricted = getDatasetRefsInDataQuery(dataQuery).some(ref => designer.isRestricted(ref.id));
                if (dataQueryRestricted)
                    return;

                const findEditorByType = (type: string): EditorBase | undefined => editorsInGroup.find(e => e.propDef.type === type);
                const findEditorByPathSuffix = (suffix: string): EditorBase | undefined => editorsInGroup.find(e => e.propDef.path?.endsWith(suffix));
                const findEditorByPropName = (propName: string): EditorBase | undefined => editorsInGroup.find(e => e?.propName === propName);

                const filtersEditor = findEditorByType('filtersEditor');
                if (filtersEditor) filtersEditor.setVisibility(true);

                // Bindings are also data-query scoped, but only apply when the design declares
                // global filters (the editor hides itself otherwise).
                const globalFilterBindingsEditor = findEditorByType('globalFilterBindings');
                if (globalFilterBindingsEditor) {
                    const design = designer._design as IDashboardDesign;
                    globalFilterBindingsEditor.setVisibility(!!design?.globalFilters?.length);
                }

                const isGroupByDefined = !!dataQuery?.aggregation?.groupBy?.columnId;
                const hasMetrics = (dataQuery?.aggregation?.metrics?.length || 0) > 0;
                const isBreakDownByDefined = !!dataQuery?.aggregation?.breakDownBy?.columnId;
                const hasProjections = (dataQuery?.projection?.columns?.length || 0) > 0;
                const hasAggregation = isGroupByDefined || hasMetrics;

                const projectionsEditor = findEditorByPathSuffix('projection');
                if (projectionsEditor) projectionsEditor.setVisibility(!hasAggregation);

                const groupByEditor = findEditorByPathSuffix('groupBy');
                if (groupByEditor) groupByEditor.setVisibility(!hasProjections);

                const metricsEditor = findEditorByType('metricsEditor');
                if (metricsEditor) {
                    const isValueLabel = component.type === ComponentType.valueLabel;
                    metricsEditor.setVisibility(isGroupByDefined || isValueLabel);
                }

                const breakDownByEditor = findEditorByPathSuffix('breakDownBy');
                if (breakDownByEditor) breakDownByEditor.setVisibility(hasMetrics);

                const breakDownByLimitEditor = findEditorByType('breakDownByLimitEditor');
                if (breakDownByLimitEditor) breakDownByLimitEditor.setVisibility(isBreakDownByDefined);

                const aggregationFiltersEditor = findEditorByPathSuffix('aggregationFilter');
                if (aggregationFiltersEditor) aggregationFiltersEditor.setVisibility(hasAggregation)

                const hasDataShape = hasAggregation || hasProjections;
                const sortOrderEditor = findEditorByPropName('sortOrder');
                if (sortOrderEditor) sortOrderEditor.setVisibility(hasDataShape);

                const displaySortOrderEditor = findEditorByPropName('displaySortOrder');
                if (displaySortOrderEditor) displaySortOrderEditor.setVisibility(hasDataShape);

                const limitEditor = findEditorByPropName('limit');
                if (limitEditor) limitEditor.setVisibility(hasDataShape);

                const offsetEditor = findEditorByPropName('offset');
                if (offsetEditor) offsetEditor.setVisibility(hasDataShape);
            });
        }
    }

    function createFormattingProperties(config: {
        groupPrefix: string;
        displayPrefix: string;
        pathRoot: string;
        visibleWhen: (c: IComponent) => boolean;
        defaultFormat?: string;
        defaultStyle?: IFontStyle;
        exclude?: string[];
        allowedFormats?: string[];
    }): { [key: string]: IPropertyDefinition } {
        const {
            groupPrefix,
            displayPrefix,
            pathRoot,
            visibleWhen,
            defaultFormat = 'Value',
            defaultStyle = { color: '#333333', size: 'Medium', decor: 'Normal' },
            exclude = [],
            allowedFormats
        } = config;

        const isValueFormat = (c: IComponent) => {
            if (!visibleWhen(c)) return false;
            const currentFormat = appn.designerv2.getPropertyByPath(c, `${pathRoot}.format`) || defaultFormat;
            return currentFormat !== 'Category';
        };

        const textStyleLabel = T('Text Style');
        const contentFormatLabel = T('Content Format');
        const prefixLabel = T('Prefix');
        const suffixLabel = T('Suffix');
        const valueFactorLabel = T('Value Factor');
        const decimalsLabel = T('Decimals');

        const props: { [key: string]: IPropertyDefinition } = {};
        props[`${groupPrefix}TextStyle`] = {
            name: `${displayPrefix} ${textStyleLabel}`,
            type: 'fontStyle',
            tooltip: T('Font settings.'),
            defaultValue: defaultStyle,
            sector: 'layout',
            path: `${pathRoot}.textStyle`,
            visibleWhen: visibleWhen
        };

        const allFormats = ['Value', 'Percent', 'Value (Percent)', 'Category', 'Category: Value', 'Category: Percent', 'Category: Value (Percent)'];
        props[`${groupPrefix}Format`] = {
            name: `${displayPrefix} ${contentFormatLabel}`,
            type: 'enum',
            options: allowedFormats || allFormats,
            sector: 'layout',
            defaultValue: defaultFormat,
            tooltip: T('What information to display.'),
            path: `${pathRoot}.format`,
            visibleWhen: visibleWhen
        };

        props[`${groupPrefix}Prefix`] = {
            name: `${displayPrefix} ${prefixLabel}`,
            type: 'text',
            defaultValue: '',
            sector: 'layout',
            tooltip: T('Text before the value.'),
            path: `${pathRoot}.prefix`,
            visibleWhen: isValueFormat
        };

        props[`${groupPrefix}Suffix`] = {
            name: `${displayPrefix} ${suffixLabel}`,
            type: 'text',
            defaultValue: '',
            sector: 'layout',
            tooltip: T('Text after the value.'),
            path: `${pathRoot}.suffix`,
            visibleWhen: isValueFormat
        };

        props[`${groupPrefix}Factor`] = {
            name: `${displayPrefix} ${valueFactorLabel}`,
            type: 'numeric',
            defaultValue: 1,
            sector: 'layout',
            tooltip: T('Multiply the value by this factor.'),
            path: `${pathRoot}.factor`,
            config: { allowDecimal: true, step: 'any' },
            visibleWhen: isValueFormat
        };

        props[`${groupPrefix}Decimals`] = {
            name: `${displayPrefix} ${decimalsLabel}`,
            type: 'numeric',
            defaultValue: 0,
            sector: 'layout',
            tooltip: T('Decimal places.'),
            path: `${pathRoot}.decimals`,
            config: { min: 0 },
            visibleWhen: isValueFormat
        };

        if (exclude.length > 0) {
            exclude.forEach(key => {
                const propKey = `${groupPrefix}${key}`;
                delete props[propKey];
            });
        }

        return props;
    }

    function bindPieGeometryConstraints() {
        const RADIUS_BUFFER = 10;

        eventBus.subscribeToPropertyUpdates(payload => {
            const path = payload.propPath;
            if (path !== 'layout.pieOuterRadius' && path !== 'layout.pieInnerRadius')
                return;

            const component = designer.getComponent(payload.componentId);
            if (!component || component.type !== 'pieChart')
                return;

            const layout = component.layout as IDashboardComponentLayout;
            if (layout?.enableCustomGeometry !== true)
                return;

            const newLayout = { ...layout };
            let changed = false;

            // Default values matching chart.ts
            const outer = newLayout.pieOuterRadius ?? 75;
            const inner = newLayout.pieInnerRadius ?? 45;

            if (path === 'layout.pieOuterRadius') {
                // User moved OUTER -> Push Inner DOWN
                const maxInner = Math.max(0, outer - RADIUS_BUFFER);
                if (inner > maxInner) {
                    newLayout.pieInnerRadius = maxInner;
                    changed = true;
                }
            }
            else if (path === 'layout.pieInnerRadius') {
                // User moved INNER -> Push Outer UP
                const minOuter = Math.min(100, inner + RADIUS_BUFFER);
                if (outer < minOuter) {
                    newLayout.pieOuterRadius = minOuter;
                    changed = true;
                    if (newLayout.pieOuterRadius === 100 && inner > (100 - RADIUS_BUFFER))
                        newLayout.pieInnerRadius = 100 - RADIUS_BUFFER;
                }
            }

            if (changed) {
                setTimeout(() => {
                    designer.updateComponentProperty(payload.componentId, 'layout', newLayout, path);
                }, 0);
            }
        });
    }
}
